/**
 * Splits the array into groups of the specified size.
 *
 * @template T - The type of elements in the array.
 * @param {T[]} arr - The array to be split into groups.
 * @param {number} groupSize - The size of each group.
 * @param {T[][]} result - The accumulator for storing the groups.
 * @param {number} index - The current index in the array.
 * @returns {T[][]} The array split into groups of the specified size.
 */
export const splitIntoGroups = <T>(arr: T[], groupSize: number, result: T[][] = [], index = 0): T[][] => {
    if (groupSize <= 0) {
        return [];
    }

    if (index >= arr.length) {
        return result;
    }

    const group = arr.slice(index, index + groupSize);
    result.push(group);

    return splitIntoGroups(arr, groupSize, result, index + groupSize);
};

/**
 * Checks if the elements from one array are matching, based on a key, elements from the second array.
 *
 * @param arr1 - The first array which has to be checked.
 * @param arr2 - The second array with the elements to check against.
 * @param key - The key to check in objects.
 * @returns  An array of elements from the first array that match elements in the second array.
 */
export const getMatchingElements = <T extends Record<string, any>>(arr1?: T[], arr2?: T[], key?: keyof T): T[] => {
    if (!arr1 || !arr2) {
        return [];
    }

    if (key) {
        const setToCheckAgainst = new Set(arr2.map((item) => item[key]));
        return arr1.filter((item) => setToCheckAgainst.has(item[key]));
    }

    const setToCheckAgainst = new Set(arr2);
    return arr1.filter((item) => setToCheckAgainst.has(item));
};
